import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Signin from "../components/Signin";
import Signup from "../components/Register";
import OTPVerification from "../components/OTP";
import API_URL from "../config/config";

const initialForm = {
  personal: {
    fullName: "",
    email: "",
    phone: "",
    city: "",
    address: "",
  },
  business: {
    legalBusinessName: "",
    tradingName: "",
    businessType: "",
    gstin: "",
    monthlyOrders: "",
    serviceArea: "",
  },
  bank: {
    accountHolderName: "",
    accountNumber: "",
    confirmAccountNumber: "",
    ifscCode: "",
    bankName: "",
    branchName: "",
  },
};

const ApiLanding = () => {
  const [showSigninModal, setShowSigninModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [mobileForOTP, setMobileForOTP] = useState("");
  const [otpIntent, setOtpIntent] = useState(null);

  const [application, setApplication] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [apiKeyMeta, setApiKeyMeta] = useState(null);
  const [revealedApiKey, setRevealedApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [documents, setDocuments] = useState({
    panCard: null,
    aadhaarCard: null,
    gstCertificate: null,
    cancelledCheque: null,
  });

  const token = localStorage.getItem("token");
  const userName = localStorage.getItem("userName") || "User";
  const isLoggedIn = Boolean(token);

  const isApproved = application?.status === "approved";
  const isPending = application?.status === "pending";

  const messageText = useMemo(() => {
    if (isPending) {
      return "Thank you for showing interest in Instaport Delivery API. Our expert team will verify your details and onboard you soon. Expected timeline: 2-3 working days.";
    }
    if (isApproved) {
      return "Your API onboarding is approved. You can now use API Dashboard and generate API key.";
    }
    if (application?.status === "rejected") {
      return `Your application was rejected. ${application?.reviewNote || ""}`.trim();
    }
    return "";
  }, [application, isApproved, isPending]);

  const authHeaders = isLoggedIn ? { Authorization: `Bearer ${token}` } : {};

  const loadPartnerData = async () => {
    if (!isLoggedIn) return;
    setLoading(true);
    try {
      const [appRes, statsRes, keyRes] = await Promise.all([
        axios.get(`${API_URL}/api-partner/me/application`, { headers: authHeaders }),
        axios.get(`${API_URL}/api-partner/me/dashboard`, { headers: authHeaders }).catch(() => null),
        axios.get(`${API_URL}/api-partner/me/keys`, { headers: authHeaders }).catch(() => null),
      ]);

      const appData = appRes?.data?.application || null;
      setApplication(appData);
      if (statsRes?.data?.stats) setDashboardStats(statsRes.data.stats);
      if (keyRes?.data?.key) setApiKeyMeta(keyRes.data.key);

      if (!appData) return;
      setFormData((prev) => ({
        personal: { ...prev.personal, ...(appData.personal || {}) },
        business: { ...prev.business, ...(appData.business || {}) },
        bank: {
          ...prev.bank,
          ...(appData.bank || {}),
          confirmAccountNumber: appData.bank?.accountNumber || "",
        },
      }));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load API onboarding data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPartnerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onFieldChange = (section, key, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const uploadDoc = async (file) => {
    const uploadBody = new FormData();
    uploadBody.append("files", file);
    uploadBody.append("path", "api-partner/");
    const res = await axios.post(`${API_URL}/upload`, uploadBody);
    return res?.data?.media?.url || "";
  };

  const validateForm = () => {
    const required = [
      formData.personal.fullName,
      formData.personal.email,
      formData.personal.phone,
      formData.personal.city,
      formData.personal.address,
      formData.business.legalBusinessName,
      formData.business.businessType,
      formData.business.monthlyOrders,
      formData.business.serviceArea,
      formData.bank.accountHolderName,
      formData.bank.accountNumber,
      formData.bank.confirmAccountNumber,
      formData.bank.ifscCode,
      formData.bank.bankName,
      formData.bank.branchName,
    ];
    if (required.some((v) => !String(v || "").trim())) {
      return "Please fill all required fields.";
    }
    if (formData.bank.accountNumber !== formData.bank.confirmAccountNumber) {
      return "Account number and confirm account number must match.";
    }
    if (!documents.panCard || !documents.aadhaarCard || !documents.gstCertificate || !documents.cancelledCheque) {
      return "Please upload all required documents.";
    }
    return "";
  };

  const submitApplication = async () => {
    if (!isLoggedIn) {
      setShowSigninModal(true);
      return;
    }
    const validationMessage = validateForm();
    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }
    setSubmitting(true);
    try {
      const [panCardUrl, aadhaarCardUrl, gstCertificateUrl, cancelledChequeUrl] =
        await Promise.all([
          uploadDoc(documents.panCard),
          uploadDoc(documents.aadhaarCard),
          uploadDoc(documents.gstCertificate),
          uploadDoc(documents.cancelledCheque),
        ]);

      await axios.post(
        `${API_URL}/api-partner/me/application`,
        {
          personal: formData.personal,
          business: formData.business,
          bank: {
            accountHolderName: formData.bank.accountHolderName,
            accountNumber: formData.bank.accountNumber,
            ifscCode: formData.bank.ifscCode,
            bankName: formData.bank.bankName,
            branchName: formData.bank.branchName,
          },
          documents: {
            panCardUrl,
            aadhaarCardUrl,
            gstCertificateUrl,
            cancelledChequeUrl,
          },
        },
        { headers: authHeaders }
      );
      toast.success("Application submitted successfully");
      await loadPartnerData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  const generateApiKey = async () => {
    try {
      await axios.post(`${API_URL}/api-partner/me/keys/generate`, {}, { headers: authHeaders });
      setRevealedApiKey("");
      toast.success("API key generated");
      await loadPartnerData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to generate key");
    }
  };

  const revealKey = async () => {
    try {
      const res = await axios.get(`${API_URL}/api-partner/me/keys/reveal`, {
        headers: authHeaders,
      });
      setRevealedApiKey(res?.data?.key || "");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to reveal key");
    }
  };

  const deleteKey = async () => {
    if (!window.confirm("Delete API key permanently?")) return;
    try {
      await axios.delete(`${API_URL}/api-partner/me/keys`, { headers: authHeaders });
      setRevealedApiKey("");
      toast.success("API key deleted");
      await loadPartnerData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete key");
    }
  };

  const copyKey = async () => {
    const value = revealedApiKey;
    if (!value) return;
    await navigator.clipboard.writeText(value);
    toast.success("API key copied");
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#fffef7] px-5 md:px-16 lg:px-24 py-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-semibold">Instaport Delivery API</h1>
            <p className="text-gray-600 mt-2">
              Integrate order creation and tracking directly from your platform.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (!isLoggedIn) {
                  setShowSigninModal(true);
                  return;
                }
                const section = document.getElementById("api-get-started");
                if (section) section.scrollIntoView({ behavior: "smooth" });
              }}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg"
            >
              Get Started
            </button>
            {!isLoggedIn ? (
              <button
                onClick={() => setShowSigninModal(true)}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white"
              >
                Login
              </button>
            ) : (
              <div className="px-4 py-2 rounded-lg border border-gray-300 bg-white">
                {userName}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 bg-white border rounded-2xl p-5 shadow-sm">
          <h2 className="text-xl font-semibold">API Home</h2>
          <p className="text-gray-600 mt-2">
            Complete verification, get approved by admin, generate your API key, and use the create-order endpoint.
          </p>
        </div>

        {loading && <p className="mt-4 text-gray-600">Loading...</p>}

        {isLoggedIn && (isPending || isApproved || application?.status === "rejected") && (
          <div className="mt-6 bg-white border rounded-2xl p-5">
            <h3 className="text-lg font-semibold capitalize">
              Verification Status: {application?.status}
            </h3>
            <p className="text-gray-600 mt-2">{messageText}</p>
          </div>
        )}

        {isLoggedIn && isApproved && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="bg-white border rounded-xl p-4">
              <p className="text-sm text-gray-500">Total API Calls</p>
              <p className="text-2xl font-semibold">{dashboardStats?.totalApiCalls || 0}</p>
            </div>
            <div className="bg-white border rounded-xl p-4">
              <p className="text-sm text-gray-500">API Orders</p>
              <p className="text-2xl font-semibold">{dashboardStats?.totalApiOrders || 0}</p>
            </div>
            <div className="bg-white border rounded-xl p-4">
              <p className="text-sm text-gray-500">Total Orders</p>
              <p className="text-2xl font-semibold">{dashboardStats?.totalOrders || 0}</p>
            </div>
            <div className="bg-white border rounded-xl p-4">
              <p className="text-sm text-gray-500">Delivered</p>
              <p className="text-2xl font-semibold">{dashboardStats?.totalDelivered || 0}</p>
            </div>
          </div>
        )}

        {isLoggedIn && isApproved && (
          <div className="mt-6 bg-white border rounded-2xl p-5">
            <h3 className="text-lg font-semibold">API Key</h3>
            {!apiKeyMeta ? (
              <button
                onClick={generateApiKey}
                className="mt-3 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg"
              >
                Generate Key
              </button>
            ) : (
              <div className="mt-3">
                <p className="text-gray-700">{revealedApiKey || apiKeyMeta.keyPreview}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={revealKey} className="px-3 py-1.5 border rounded-lg bg-white">
                    Eye
                  </button>
                  <button onClick={copyKey} className="px-3 py-1.5 border rounded-lg bg-white">
                    Copy
                  </button>
                  <button
                    onClick={deleteKey}
                    className="px-3 py-1.5 border rounded-lg bg-red-50 text-red-600 border-red-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {isLoggedIn && isApproved && (
          <div className="mt-6 bg-white border rounded-2xl p-5">
            <h3 className="text-lg font-semibold">Documentation</h3>
            <p className="text-gray-600 mt-2">Create order using API key.</p>
            <pre className="mt-3 bg-gray-50 p-3 rounded-lg text-xs overflow-auto">
{`POST ${API_URL}/api-partner/orders/create
Headers:
  x-api-key: <your_api_key>
  Content-Type: application/json

Body (sample):
{
  "pickup": { "text": "Pickup", "address": "Pickup", "latitude": 19.1, "longitude": 72.8, "key": "pickup-key" },
  "drop": { "text": "Drop", "address": "Drop", "latitude": 19.2, "longitude": 72.9, "key": "drop-key" },
  "locations": [ ... ],
  "parcel_weight": "0-1 kg",
  "parcel_value": 500,
  "package": "Documents",
  "payment_method": "online",
  "amount": 80,
  "security_fee": 5,
  "security_fee_percent": 1,
  "commission": 20,
  "delivery_type": "now"
}`}
            </pre>
          </div>
        )}

        {!isLoggedIn && (
          <div id="api-get-started" className="mt-8 bg-white border rounded-2xl p-5">
            <p className="text-gray-700">
              Please login to start API onboarding.
            </p>
            <button
              onClick={() => setShowSigninModal(true)}
              className="mt-3 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg"
            >
              Login
            </button>
          </div>
        )}

        {isLoggedIn && !application && (
          <div id="api-get-started" className="mt-8 bg-white border rounded-2xl p-5">
            <h3 className="text-lg font-semibold">Get Started - Verification Form</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <input className="border rounded-lg p-2.5" placeholder="Full Name" value={formData.personal.fullName} onChange={(e) => onFieldChange("personal", "fullName", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="Email" value={formData.personal.email} onChange={(e) => onFieldChange("personal", "email", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="Phone" value={formData.personal.phone} onChange={(e) => onFieldChange("personal", "phone", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="City" value={formData.personal.city} onChange={(e) => onFieldChange("personal", "city", e.target.value)} />
              <input className="border rounded-lg p-2.5 md:col-span-2" placeholder="Address" value={formData.personal.address} onChange={(e) => onFieldChange("personal", "address", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="Legal Business Name" value={formData.business.legalBusinessName} onChange={(e) => onFieldChange("business", "legalBusinessName", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="Trading Name" value={formData.business.tradingName} onChange={(e) => onFieldChange("business", "tradingName", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="Business Type" value={formData.business.businessType} onChange={(e) => onFieldChange("business", "businessType", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="GSTIN" value={formData.business.gstin} onChange={(e) => onFieldChange("business", "gstin", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="Monthly Orders" value={formData.business.monthlyOrders} onChange={(e) => onFieldChange("business", "monthlyOrders", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="Service Area" value={formData.business.serviceArea} onChange={(e) => onFieldChange("business", "serviceArea", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="Account Holder Name" value={formData.bank.accountHolderName} onChange={(e) => onFieldChange("bank", "accountHolderName", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="Account Number" value={formData.bank.accountNumber} onChange={(e) => onFieldChange("bank", "accountNumber", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="Confirm Account Number" value={formData.bank.confirmAccountNumber} onChange={(e) => onFieldChange("bank", "confirmAccountNumber", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="IFSC Code" value={formData.bank.ifscCode} onChange={(e) => onFieldChange("bank", "ifscCode", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="Bank Name" value={formData.bank.bankName} onChange={(e) => onFieldChange("bank", "bankName", e.target.value)} />
              <input className="border rounded-lg p-2.5" placeholder="Branch Name" value={formData.bank.branchName} onChange={(e) => onFieldChange("bank", "branchName", e.target.value)} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <input type="file" onChange={(e) => setDocuments((p) => ({ ...p, panCard: e.target.files?.[0] || null }))} />
              <input type="file" onChange={(e) => setDocuments((p) => ({ ...p, aadhaarCard: e.target.files?.[0] || null }))} />
              <input type="file" onChange={(e) => setDocuments((p) => ({ ...p, gstCertificate: e.target.files?.[0] || null }))} />
              <input type="file" onChange={(e) => setDocuments((p) => ({ ...p, cancelledCheque: e.target.files?.[0] || null }))} />
            </div>

            <button
              onClick={submitApplication}
              disabled={submitting}
              className="mt-5 bg-yellow-500 hover:bg-yellow-600 disabled:opacity-60 text-white px-4 py-2 rounded-lg"
            >
              {submitting ? "Submitting..." : "Submit Details"}
            </button>
          </div>
        )}
      </div>
      <Footer />

      <Signin
        setOtpIntent={setOtpIntent}
        showSigninModal={showSigninModal}
        setShowSigninModal={setShowSigninModal}
        setShowSignupModal={setShowSignupModal}
        setShowOTPModal={setShowOTPModal}
        setMobileForOTP={setMobileForOTP}
      />
      <Signup
        setOtpIntent={setOtpIntent}
        showSignupModal={showSignupModal}
        setShowSignupModal={setShowSignupModal}
        setShowSigninModal={setShowSigninModal}
        setShowOTPModal={setShowOTPModal}
        setMobileForOTP={setMobileForOTP}
      />
      <OTPVerification
        otpIntent={otpIntent}
        setShowSignupModal={setShowSignupModal}
        showOTPModal={showOTPModal}
        setShowOTPModal={setShowOTPModal}
        setShowSigninModal={setShowSigninModal}
        mobile={mobileForOTP}
      />
    </>
  );
};

export default ApiLanding;
